import { useDroppable } from '@dnd-kit/core'
import { SortableContext } from '@dnd-kit/sortable'
import { format } from 'date-fns'
import { LayoutTemplate, Monitor, MousePointerSquareDashed, SlidersHorizontal, Smartphone, Tablet, type LucideIcon } from 'lucide-react'
import type { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { WidgetGrid } from '../components/WidgetGrid'
import { gridClass, gridStyle, type GridColumns } from '../layout/grid'
import { CanvasWidget } from './CanvasWidget'
import { CANVAS_DROP_ID, liveReorderStrategy } from './useEditorDnd'
import type { LayoutEditor } from './useLayoutEditor'

export type Device = 'desktop' | 'tablet' | 'mobile'

type DeviceSpec = { icon: LucideIcon; cols: GridColumns; maxWidth?: number }

const DEVICE_SPECS: Record<Device, DeviceSpec> = {
  desktop: { icon: Monitor, cols: 4 },
  tablet: { icon: Tablet, cols: 2, maxWidth: 720 },
  mobile: { icon: Smartphone, cols: 1, maxWidth: 380 },
}
const DEVICES = (Object.keys(DEVICE_SPECS) as Device[]).map((key) => ({ key, ...DEVICE_SPECS[key] }))

type Props = {
  editor: LayoutEditor
  placeholderId: string | null
  dragging: boolean
  device: Device
  onDeviceChange: (d: Device) => void
  preview: boolean
  onSuggested: () => void
  onRemove: (id: string) => void
  onOpenProperties?: () => void
}

export function EditorCanvas({ editor, placeholderId, dragging, device, onDeviceChange, preview, onSuggested, onRemove, onOpenProperties }: Props) {
  const { t } = useTranslation()
  const { setNodeRef, isOver } = useDroppable({ id: CANVAS_DROP_ID, disabled: preview })
  const current = DEVICE_SPECS[device]
  const { widgets } = editor
  const deselect = (e: MouseEvent) => {
    if (e.target === e.currentTarget) editor.select(null)
  }

  return (
    <section className="flex min-w-0 flex-col rounded-2xl border bg-muted/30 dark:bg-muted/10">
      <header className="flex flex-wrap items-center gap-2 border-b border-border/60 px-3 py-2.5">
        <h2 className="text-sm font-semibold">{t(preview ? 'dashboard.edit.previewTitle' : 'dashboard.edit.canvas')}</h2>
        {editor.draftAt ? (
          <span className="rounded-full bg-success/12 px-2 py-0.5 text-[11px] font-medium text-success">
            {t('dashboard.edit.draftSaved', { time: format(editor.draftAt, 'HH:mm') })}
          </span>
        ) : null}
        <div className="ml-auto flex items-center gap-1.5">
          {!preview && onOpenProperties ? (
            <Button size="sm" className="h-8" onClick={onOpenProperties}>
              <SlidersHorizontal />
              {t('dashboard.edit.properties')}
            </Button>
          ) : null}
          {!preview ? (
            <Button variant="outline" size="sm" className="h-8" onClick={onSuggested}>
              <LayoutTemplate />
              <span className="hidden sm:inline">{t('dashboard.edit.suggested')}</span>
            </Button>
          ) : null}
          <div role="radiogroup" aria-label={t('dashboard.edit.device')} className="flex items-center rounded-lg border bg-card p-0.5">
            {DEVICES.map(({ key, icon: Icon }) => (
              <Tooltip key={key} content={t(`dashboard.edit.devices.${key}`)} side="bottom">
                <button
                  type="button"
                  role="radio"
                  aria-checked={device === key}
                  aria-label={t(`dashboard.edit.devices.${key}`)}
                  onClick={() => onDeviceChange(key)}
                  className={cn(
                    'flex size-7 items-center justify-center rounded-md transition-colors',
                    device === key ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <Icon className="size-4" />
                </button>
              </Tooltip>
            ))}
          </div>
        </div>
      </header>

      <div className="min-w-0 flex-1 p-3 sm:p-4">
        <div className="mx-auto w-full transition-[max-width] duration-300" style={{ maxWidth: current.maxWidth }}>
          {preview ? (
            <WidgetGrid widgets={widgets} cols={current.cols} />
          ) : (
            <div
              ref={setNodeRef}
              onClick={deselect}
              className={cn(
                'min-h-80 rounded-2xl border-2 border-dashed p-3 transition-colors',
                isOver || dragging ? 'border-primary/40 bg-primary/[0.03]' : 'border-border/80',
              )}
            >
              {widgets.length === 0 ? (
                <div className="flex min-h-72 flex-col items-center justify-center gap-2 text-center text-muted-foreground">
                  <MousePointerSquareDashed className="size-6" />
                  <p className="text-sm font-medium text-foreground">{t('dashboard.edit.emptyCanvas')}</p>
                  <p className="max-w-xs text-xs">{t('dashboard.edit.emptyCanvasHint')}</p>
                </div>
              ) : (
                <SortableContext items={widgets.map((w) => w.id)} strategy={liveReorderStrategy}>
                  <div className={gridClass(current.cols)} style={gridStyle} onClick={deselect}>
                    {widgets.map((w) => (
                      <CanvasWidget
                        key={w.id}
                        widget={w}
                        cols={current.cols}
                        selected={editor.selectedId === w.id}
                        placeholder={placeholderId === w.id}
                        onSelect={() => editor.select(w.id)}
                        onRemove={() => onRemove(w.id)}
                        onResize={(size) => editor.update(w.id, size)}
                      />
                    ))}
                  </div>
                </SortableContext>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
